type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function sortedOwnKeys(value: UnknownRecord): string[] {
  return Object.keys(value).sort();
}

function graphqlErrors(value: unknown): UnknownRecord[] {
  const candidates = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.errors)
      ? value.errors
      : [];
  return candidates.filter(isRecord);
}

function describeGraphqlError(error: UnknownRecord): Record<string, unknown> {
  const extensions = isRecord(error.extensions) ? error.extensions : undefined;
  const extras = isRecord(error.extras) ? error.extras : undefined;
  return {
    ownKeys: sortedOwnKeys(error),
    ...(nonEmptyString(error.message) ? { message: error.message } : {}),
    ...(nonEmptyString(error.errorType) ? { errorType: error.errorType } : {}),
    ...(extensions
      ? {
          ...(nonEmptyString(extensions.code) ? { extensionsCode: extensions.code } : {}),
          extensionsKeys: sortedOwnKeys(extensions),
        }
      : {}),
    ...(extras ? { extrasKeys: sortedOwnKeys(extras) } : {}),
  };
}

export function getErrorMessage(error: unknown, fallback: string): string {
  const direct = error instanceof Error ? error.message : isRecord(error) ? error.message : error;
  const message = nonEmptyString(direct);
  if (message) return message;
  const messages = graphqlErrors(error)
    .map((item) => nonEmptyString(item.message))
    .filter((item): item is string => item !== undefined);
  return messages.length > 0 ? messages.join('; ') : fallback;
}

export function describeError(error: unknown): Record<string, unknown> {
  const record = isRecord(error) ? error : undefined;
  const exception = error instanceof Error ? error : undefined;
  const graphQLErrors = graphqlErrors(error);
  const message = nonEmptyString(exception?.message) ?? nonEmptyString(record?.message);
  const stack = nonEmptyString(exception?.stack);
  return {
    valueType: error === null ? 'null' : Array.isArray(error) ? 'array' : typeof error,
    ...(record ? { ownKeys: sortedOwnKeys(record) } : {}),
    ...(exception?.name ? { errorName: exception.name } : {}),
    ...(message ? { errorMessage: message } : {}),
    ...(stack ? { stack } : {}),
    ...(graphQLErrors.length > 0 ? { graphqlErrors: graphQLErrors.map(describeGraphqlError) } : {}),
  };
}
